/**
 * Ask Bower Footer
 *
 * A pinned footer at the bottom of the Cmd-K palette — always visible, so the
 * "hand this off to Bower" escape hatch never scrolls away behind a long list of
 * results. It lives OUTSIDE the scrollable `[cmdk-list]`, so it stays put while
 * results scroll. Activated by click or the palette's Tab shortcut; the actual
 * `chat:openPanel` dispatch lives in the palette's `onActivate` handler so the
 * Tab and click paths share one implementation.
 */

import { Box, HStack, Kbd, Text } from '@chakra-ui/react';
import { MaterialIcon } from '../../ui/MaterialIcon';

interface AskBirdFooterProps {
  query: string;
  onActivate: () => void;
}

export function AskBirdFooter({ query, onActivate }: AskBirdFooterProps) {
  const label = query ? `Ask Bower about "${query}"` : 'Ask Bower';

  return (
    <Box
      data-palette-footer
      as="button"
      type="button"
      onClick={onActivate}
      w="100%"
      textAlign="left"
      // A parchment band, not the twilight tint. The footer is the sheet's
      // floor rather than an accent: the sparkle is what marks it as the AI
      // route, and a tinted band under a white sheet competes with the
      // filter button above for the one accent in the modal.
      bg="bg.surface"
      borderTop="1px solid"
      borderColor="border.subtle"
      cursor="pointer"
      _hover={{ bg: 'bg.hover' }}
      _active={{ bg: 'bg.subtle' }}
      aria-label={query ? `Ask Bower about ${query}` : 'Ask Bower'}
    >
      <HStack px={6} py="14px" spacing={3} align="center">
        <MaterialIcon name="auto_awesome" boxSize={5} gradient="warm" aria-hidden />

        <Text
          fontSize="sm"
          fontWeight="semibold"
          color="text.primary"
          noOfLines={1}
          flex={1}
          minW={0}
        >
          {label}
        </Text>

        {/* `bg.elevated`, not the themed default: this cap sits ON the
            parchment footer band, so the default parchment fill would vanish
            into it. Same cap, one surface-aware override. */}
        <Kbd bg="bg.elevated">Tab</Kbd>
      </HStack>
    </Box>
  );
}
